import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { IExaminationDataValue } from "@/models/ExaminationDataValue";
import { IExamination } from "@/models/Examination";
import { ICorrection } from "@/models/Correction";
import { ExaminationStatus } from "@/root/types";

export interface CurrentDataState {
  examinationData: IExaminationDataValue[];
  correction: ICorrection | null;
  currentData: IExaminationDataValue | null;
  currentExaminationData: IExaminationDataValue | null;
  activeExamination: IExamination | null;
  chosenExamination: IExamination | null;
  examinationStatus: ExaminationStatus;
  plcError: string;
  loggedErrors: string[];
  unrecordedExaminationError: boolean;
  m15: boolean;
}

const initialState: CurrentDataState = {
  examinationData: [],
  correction: null,
  currentData: null,
  currentExaminationData: null,
  activeExamination: null,
  chosenExamination: null,
  examinationStatus: ExaminationStatus.default,
  plcError: "",
  loggedErrors: [],
  unrecordedExaminationError: false,
  m15: false,
};

export const currentDataSlice = createSlice({
  name: "stand",
  initialState,
  reducers: {
    setInitExaminationData: (
      state,
      action: PayloadAction<IExaminationDataValue[]>
    ) => {
      state.examinationData = action.payload;
    },
    addExaminationData: (
      state,
      action: PayloadAction<IExaminationDataValue[]>
    ) => {
      state.examinationData = [
        ...state.examinationData,
        ...action.payload,
      ].filter(
        (value, index, self) =>
          index === self.findIndex((t) => t.Time === value.Time)
      );
    },
    setCurrentDataValue: (
      state,
      action: PayloadAction<IExaminationDataValue>
    ) => {
      state.currentData = action.payload;
    },
    setCurrentExaminationData: (
      state,
      action: PayloadAction<IExaminationDataValue>
    ) => {
      state.currentExaminationData = action.payload;
    },
    clearExaminationData: (state) => {
      state.examinationData = [];
      state.currentExaminationData = null;
    },
    setCorrection: (state, action: PayloadAction<ICorrection>) => {
      state.correction = action.payload;
    },
    setActiveExamination: (
      state,
      action: PayloadAction<IExamination | null>
    ) => {
      state.activeExamination = action.payload;
    },
    setChosenExamination: (
      state,
      action: PayloadAction<IExamination | null>
    ) => {
      state.examinationData = [];
      state.chosenExamination = action.payload;
    },
    setExaminationStatus: (state, action: PayloadAction<ExaminationStatus>) => {
      state.examinationStatus = action.payload;
    },
    setPlcError: (state, action: PayloadAction<string>) => {
      state.plcError = action.payload;
    },
    logPlcError: (state, action: PayloadAction<string>) => {
      state.loggedErrors.push(action.payload);
    },
    clearPlcErrors: (state) => {
      state.plcError = "";
      state.loggedErrors = [];
    },
    setUnrecordedExaminationError: (state, action: PayloadAction<boolean>) => {
      state.unrecordedExaminationError = action.payload;
    },
    setM15: (state, action: PayloadAction<boolean>) => {
      if (!action.payload && state.m15) {
        state.examinationStatus = ExaminationStatus.finished;
      }
      state.m15 = action.payload;
    },
    reset: () => initialState,
  },
});

export const {
  setInitExaminationData,
  addExaminationData,
  setCurrentDataValue,
  setCurrentExaminationData,
  clearExaminationData,
  setCorrection,
  setActiveExamination,
  setChosenExamination,
  setExaminationStatus,
  setPlcError,
  logPlcError,
  clearPlcErrors,
  setUnrecordedExaminationError,
  setM15,
  reset: resetCurrentDataState,
} = currentDataSlice.actions;

export default currentDataSlice.reducer;
